import { useTranslation } from "react-i18next";
import type { ProjectedHumanStep } from "../../../api/threads/liveMessages";
import Chip from "../../../ui/feedback/Chip";
import SpinnerIcon from "../../../ui/feedback/SpinnerIcon";
import type { ChipTone } from "../../../ui/styles/chipStyles";
import { hostTurnText, requestWaitText } from "./requestWaitWords";
import type { ThreadDisplayStatus } from "./threadStatus";

/**
 * His colours (Johannes, 4 Nov 09:28; accepted 09:41 and 09:43): needs input blue, pending approval with it, failed red;
 * working and waiting stay blue, closed grey. A done Run is green until its viewer opened it (`seen`), then grey.
 */
const STATUS_TONE: Readonly<Record<ThreadDisplayStatus, ChipTone>> = {
  working: "info",
  waiting: "info",
  offline: "info",
  approval: "info",
  input: "info",
  ready: "info",
  failed: "danger",
  closed: "neutral",
  done: "neutral",
};

interface ThreadStatusBadgeProps {
  status: ThreadDisplayStatus;
  /** The person step that waits, from the same projection: Input then says whom it waits for. */
  waiting?: ProjectedHumanStep | null;
  /** The host's name, `null` when the viewer hosts: where the turn is the host's, the badge names them. */
  hostName?: string | null;
  /** The viewer has opened the Run since it last changed (T3 Code's `hasUnseenCompletion`); unknown reads as seen. */
  seen?: boolean;
  compact?: boolean;
  className?: string;
}

/** One status per Thread, the same in the list and on the page: its progress, as module C decides it. */
export default function ThreadStatusBadge({ status, waiting = null, hostName = null, seen = true, compact = false, className }: ThreadStatusBadgeProps) {
  const { t } = useTranslation();
  const turn = hostTurnText(status, waiting, hostName, t);
  return (
    <Chip
      tone={status === "done" && !seen ? "success" : STATUS_TONE[status]}
      size={compact ? "compact" : "small"}
      title={status === "input" && waiting !== null ? requestWaitText(waiting, null, hostName, t) : t(`threads.status.${status}.explanation`)}
      icon={status === "working" ? <SpinnerIcon className="h-3 w-3" /> : null}
      className={className === undefined ? "shrink-0" : `shrink-0 ${className}`}
    >
      {turn ?? t(`threads.status.${status}.label`)}
    </Chip>
  );
}
